/**
 * This module initializes Sentry for the browser, providing error tracking
 * and performance monitoring.
 *
 * We only want to capture unhandled exceptions, so we filter out all other
 * events.
 *
 * There is also a release property that is set to the name and version of the
 * project. This is used to group errors by specific release in Sentry, so that
 * we can easily see which errors are happening in which version of the project
 * and correlate them with the code changes and appropiate commits.
 *
 * The following integrations are enabled:
 * - Browser Tracing: Provides performance monitoring
 * - Capture Console: Captures console errors
 * - Replay: Captures user interactions and uploads short videos to Sentry
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/vue/
 */
import { CaptureConsole } from "@sentry/integrations";
import * as Sentry from "@sentry/vue";

import { version } from "~/../package.json";
import { getOnlyUnhandledExceptions } from "~/utils/sentry";

export default defineNuxtPlugin({
  enforce: "pre", // Run this plugin before all other plugins

  setup(nuxtApp) {
    const router = useRouter();
    const config = useRuntimeConfig();
    const { environment, sentry } = config.public;

    if (!sentry.enabled) {
      console.warn("Sentry not enabled, skipping Sentry client initialization");
      return;
    }

    if (!sentry.dsn) {
      console.warn("Sentry DSN not set, skipping Sentry client initialization");
      return;
    }

    Sentry.init({
      app: nuxtApp.vueApp,
      dsn: sentry.dsn,
      environment,
      release: `${sentry.project}@${version}`,
      integrations: [
        new Sentry.BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router, { routeLabel: "path" }),
        }),
        new Sentry.Replay({
          maskAllText: false,
          blockAllMedia: false,
        }),
        new CaptureConsole({ levels: ["error"] }),
      ],

      // Attach stack traces to console errors for better debugging
      attachStacktrace: true,
      attachProps: true,

      // Monitor component lifecycle events and durations
      trackComponents: true,
      hooks: ["activate", "create", "destroy", "mount", "update"],

      // This is a probability of capturing a trace. Set this value to 1.0 to
      // capture 100% of transactions for performance monitoring. It's recommend
      // to adjust this value in production, otherwise a transaction will be sent
      // every time a user loads a page or navigates within your app.
      tracesSampleRate: 0.1, // Sample only 10% of transactions

      // Enable session replay: captures 10% of sessions, 100% if an error occurs
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      /**
       * Filters events before sending them to Sentry, allowing only unhandled exceptions.
       */
      beforeSend(event) {
        return getOnlyUnhandledExceptions(event);
      },
    });
  },
});
